import { useEffect, useState } from "react";

/** Nextjs */
import dynamic from "next/dynamic";

/** MUI */
import { Close, ExpandLess, ExpandMore, Settings, SettingsOutlined, Sort } from "@mui/icons-material";
import {
    Alert,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Divider,
    Drawer,
    Grid,
    IconButton,
    Typography,
    useMediaQuery,
} from "@mui/material";
import { makeStyles } from "@mui/styles";

/** Redux */
import { useDispatch, useSelector } from "react-redux";
import { getDimensions, getInfoTypes, getMultiSections } from "../../../../_appStore/DB_Filters_Table";
import {
    getLayout,
    getLayoutDefault,
    getOptions,
    getOptionsDefault,
    getSorting,
    getSortingDefault,
    setLayout,
    setOptions,
    setSorting,
} from "../../../../_appStore/DB_Settings_Table";

import { getLimits, getPrediction } from "../../../../_appStore/DB_Common";
import { getDatesSelected } from "../../../../_appStore/DB_Dates";
import { getSelectedIndicators } from "../../../../_appStore/DB_Indicators";
import { getSelectedTerritorialUnitsItems } from "../../../../_appStore/DB_JTs";
import {
    getSelectedJT,
    getSelectedSC,
    getSelectedWSK,
    setSelectedJT,
    setSelectedSC,
    setSelectedWSK,
} from "../../../../_appStore/DB_Lists_Table";
import { getDbSettingsDnD } from "@/src/_appStore/Common";

/** Helpers */
import { useTranslation } from "react-i18next";

/** UI */
// const TableLayoutSettingDnD = dynamic(() => import("./TableLayoutSettingsDnD/TableLayoutSettingsDnD"));
// const TableSortingSettingDnD = dynamic(() => import("./TableSortingSettingsDnD/TableSortingSettingsDnD"));
import TableLayoutSettingDnD from "./TableLayoutSettingsDnD/TableLayoutSettingsDnD";
import TableSortingSettingDnD from "./TableSortingSettingsDnD/TableSortingSettingsDnD";

const TableLayoutSetting = dynamic(() => import("./TableLayoutSettings"));
const TableSortingSetting = dynamic(() => import("./TableSortingSettings"));
const AppTooltip = dynamic(() => import("../../../../_common/AppTooltip"));
const TableOptionsSetting = dynamic(() => import("./TableOptionsSettings"));

const useStyles = makeStyles((theme) => ({
    MenuRoot: {},
    DrawerRoot: {
        zIndex: 0,
    },
    DrawerPaper: {
        ...theme.shape.boxedWrapper,
        borderRadius: theme.spacing(2.5, 0, 0, 2.5),
        maxHeight: "calc(100% - 16px)",
        bottom: 8,
        top: "auto",
        padding: theme.spacing(2, 2, 0, 2),
        [theme.breakpoints.down("lg")]: {
            maxHeight: "calc(100% - 16px)",
            bottom: 8,
        },
        [theme.breakpoints.down("sm")]: {
            maxHeight: "calc(100% - 8px)",
            bottom: 4,
            padding: theme.spacing(2, 0, 0, 0),
        },
    },
    layoutBox: {
        width: 1014,
        maxWidth: "calc(100vw - 36px)",
        flexGrow: 1,
        height: "100%",
        [theme.breakpoints.down("lg")]: {
            maxWidth: 480,
        },
        [theme.breakpoints.down("sm")]: {
            maxWidth: "calc(100vw - 2px)",
        },
    },
    Card: {
        height: "100%",
        position: "relative",
    },
    CardHeader: {
        display: "flex",
        alignItems: "center",
        width: "100%",
        paddingLeft: 0,
        paddingTop: 0,
        [theme.breakpoints.down("lg")]: {
            padding: theme.spacing(0, 1, 1, 1),
        },
        borderBottom: "1px solid rgba(0,0,0,0.0675)",
    },
    CardHeaderActions: {},
    CardHeaderTitle: {
        display: "flex",
        alignItems: "center",
        marginBottom: theme.spacing(0),
        fontSize: "1.33333334rem",
        fontWeight: 600,
        [theme.breakpoints.down("lg")]: {
            marginBottom: theme.spacing(0),
            fontSize: "1.11111111rem",
        },
    },
    CardContent: {
        width: "100%",
        maxHeight: "calc(100% - 148px)",
        overflow: "hidden",
        overflowY: "auto",
        // padding: theme.spacing(5, 0.5, 10.5, 0.5),
        padding: theme.spacing(5, 0.5, 0, 0.5),
        [theme.breakpoints.down("lg")]: {
            maxHeight: "calc(100% - 116px)",
            padding: theme.spacing(1, 1, 2, 1),
        },
        [theme.breakpoints.down("sm")]: {
            maxHeight: "calc(100% - 112px)",
            padding: theme.spacing(0, 1, 2, 1),
        },
    },
    CardActions: {
        width: "100%",
        position: "absolute",
        bottom: 0,
        padding: theme.spacing(3, 1),
        justifyContent: "flex-end",
        borderTop: "1px solid rgba(0,0,0,0.0675)",
        backgroundColor: "#fff",
        "& button": {
            minWidth: 140,
        },
        "& button + button": {
            marginLeft: theme.spacing(1),
        },
        [theme.breakpoints.down("sm")]: {
            padding: theme.spacing(1, 0),
        },
    },
    sectionTitle: {
        fontSize: "1.11111rem",
        fontWeight: 600,
        color: theme.palette.primary.main,
        marginTop: theme.spacing(0),
        // marginBottom: theme.spacing(3),
        flexGrow: 1,
        [theme.breakpoints.down("lg")]: {
            marginTop: theme.spacing(1),
            marginBottom: theme.spacing(2),
            paddingLeft: theme.spacing(0.5),
            fontSize: "1rem",
        },
        [theme.breakpoints.down("sm")]: {
            marginBottom: theme.spacing(2),
        },
    },
    sectionWrap: {
        ...theme.shape.boxedWrapper,
        padding: theme.spacing(1, 3, 1.5, 3),
    },
    sectionSortWrap: {
        ...theme.shape.boxedWrapper,
        padding: theme.spacing(1, 3, 3, 3),
        marginBottom: theme.spacing(5)
    },
}));

const TableSettings = (props) => {
    const { headData, isToBeApplied, onPredictionData, type = "options" } = props;
    // process.env.NODE_ENV === "development" && console.log('TableSettings', {props})

    const classes = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const downSm = useMediaQuery((theme) => theme.breakpoints.down("sm"));
    const downLg = useMediaQuery((theme) => theme.breakpoints.down("lg"));

    /** States */
    const [SETTINGS_OPEN, setSETTINGS_OPEN] = useState(false);
    const [LAYOUT, setLAYOUT] = useState(null);
    const [OPTIONS, setOPTIONS] = useState(null);
    const [SORTING, setSORTING] = useState(null);
    const [RECORDS_COUNT, setRECORDS_COUNT] = useState([]);
    const [IS_BTN_DISABLED, setIS_BTN_DISABLED] = useState({ reset: false, accept: false });
    const [COLLAPSED, setCOLLAPSED] = useState({ layout: false, options: false, sorting: false });
    const [CLOSE_TOOLTIP, setCLOSE_TOOLTIP] = useState(false);
    /** /end States */

    /** Redux selectors */
    const layout = useSelector(getLayout);
    const layoutDefault = useSelector(getLayoutDefault);
    const options = useSelector(getOptions);
    const optionsDefault = useSelector(getOptionsDefault);
    const sorting = useSelector(getSorting);
    const sortingDefault = useSelector(getSortingDefault);

    const prediction = useSelector(getPrediction);

    const types = useSelector(getInfoTypes);
    const dimensions = useSelector(getDimensions);
    const selectedDates = useSelector(getDatesSelected);
    const multiSections = useSelector(getMultiSections);
    const selectedIndicators = useSelector(getSelectedIndicators);
    const selectedJTs = useSelector(getSelectedTerritorialUnitsItems);
    const limits = useSelector(getLimits);
    const listJT = useSelector(getSelectedJT);
    const listSC = useSelector(getSelectedSC);
    const listWSK = useSelector(getSelectedWSK);

    const dbSettingsDnD = useSelector(getDbSettingsDnD);

    /** /end Redux selectors */

    /** Effects */
    /** - set current layout */
    useEffect(() => {
        if (!layout || LAYOUT) return;
        setLAYOUT(JSON.parse(JSON.stringify(layout)));
    }, [layout, LAYOUT]);

    /** - set current options */
    useEffect(() => {
        if (!options || OPTIONS) return;
        setOPTIONS(JSON.parse(JSON.stringify(options)));
    }, [options, OPTIONS]);

    /** - set current sorting */
    useEffect(() => {
        if (!sorting || JSON.parse(JSON.stringify(sorting)) === JSON.parse(JSON.stringify(SORTING))) return;
        setSORTING(structuredClone(sorting));
    }, [sorting, SETTINGS_OPEN]);

    /** - buttons state */
    useEffect(() => {
        if (layout && layoutDefault && LAYOUT && options && optionsDefault && OPTIONS && sorting && SORTING) {
            const btns_state = {
                reset: false,
                accept:
                    JSON.stringify(Object.fromEntries(Object.entries(layout).sort())) ===
                        JSON.stringify(Object.fromEntries(Object.entries(LAYOUT).sort())) &&
                    JSON.stringify(Object.fromEntries(Object.entries(options).sort())) ===
                        JSON.stringify(Object.fromEntries(Object.entries(OPTIONS).sort())) &&
                    JSON.stringify(Object.fromEntries(Object.entries(sorting).sort())) ===
                        JSON.stringify(Object.fromEntries(Object.entries(SORTING).sort())),
            };
            setIS_BTN_DISABLED(btns_state);
        }
    }, [layout, layoutDefault, LAYOUT, options, optionsDefault, OPTIONS, sorting, SORTING]);

    /** Count */
    useEffect(() => {
        if (
            !types ||
            !dimensions ||
            !selectedDates ||
            !multiSections ||
            !selectedIndicators ||
            !selectedJTs ||
            !LAYOUT ||
            !OPTIONS
        )
            return;

        let _listJT, _listSC, _listWSK;

        if (LAYOUT?.list?.length) {
            switch (LAYOUT.list[0]) {
                case "JT":
                    _listJT = listJT || selectedJTs[0];
                    _listSC = null;
                    _listWSK = null;
                    break;
                case "SC":
                    _listJT = null;
                    _listSC = listSC || selectedDates[0];
                    _listWSK = null;
                    break;
                case "Zm":
                    _listJT = null;
                    _listSC = null;
                    _listWSK = listWSK || selectedIndicators[0];
                    break;

                default:
                    _listJT = null;
                    _listSC = null;
                    _listWSK = null;
                    break;
            }
        }

        onPredictionData({
            types,
            dimensions,
            dates: selectedDates,
            sections: multiSections,
            indicators: selectedIndicators,
            jts: selectedJTs,
            layout: LAYOUT,
            options: OPTIONS,
            limits,
            tableOrChart: "table",
            from: "TableSettings",
            listJT: _listJT,
            listSC: _listSC,
            listWSK: _listWSK,
        });
    }, [
        types,
        dimensions,
        selectedDates,
        multiSections,
        selectedIndicators,
        selectedJTs,
        LAYOUT,
        OPTIONS,
        limits,
        SETTINGS_OPEN,
    ]);

    useEffect(() => {
        setRECORDS_COUNT(prediction?.table);
    }, [prediction]);
    /** /end Effects */

    /** Handlers */
    const handleOpenSettings = () => {
        if (SETTINGS_OPEN) {
            /** Restore without saving */
            setLAYOUT(layout);
            setOPTIONS(options);
            setSORTING(sorting);
        }

        setSETTINGS_OPEN(!SETTINGS_OPEN);
    };

    const handleChangeLayout = ({ newLayout }) => setLAYOUT(newLayout);

    const handleChangeOptions = ({ newOptions }) => setOPTIONS(newOptions);

    const handleChangeSorting = ({ newSorting }) => setSORTING(newSorting);

    const handleApply = () => {
        const _clearSelects = {
            JT: () => dispatch(setSelectedJT(null)),
            SC: () => dispatch(setSelectedSC(null)),
            Zm: () => dispatch(setSelectedWSK(null)),
        };

        Object.keys(_clearSelects)
            .filter((key) => key !== LAYOUT.list[0])
            .forEach((key) => _clearSelects[key]());

        dispatch(setLayout(LAYOUT));
        dispatch(setOptions(OPTIONS));
        // dispatch(setSorting(SORTING));

        if (JSON.stringify(layout) !== JSON.stringify(LAYOUT)) {
            setSORTING(null);
            dispatch(setSorting(sortingDefault));
        }

        setSETTINGS_OPEN(false);
    };
    const handleApplySort = () => {
        dispatch(setSorting(SORTING));

        setSETTINGS_OPEN(false);
    };

    const handleReset = () => {
        setLAYOUT(null);
        setOPTIONS(null);
        // setSORTING(null);

        if (JSON.stringify(layout) !== JSON.stringify(LAYOUT)) {
            setSORTING(null);
        }

        setTimeout(() => {
            setLAYOUT(layoutDefault);
            setOPTIONS(optionsDefault);
            // setSORTING(sortingDefault);
            if (JSON.stringify(layoutDefault) !== JSON.stringify(LAYOUT)) {
                setSORTING(sortingDefault);
            }
        }, 123);
    };

    const handleResetSort = () => {
        // setLAYOUT(null);
        // setOPTIONS(null);
        setSORTING(null);

        setTimeout(() => {
            // setLAYOUT(layoutDefault);
            // setOPTIONS(optionsDefault);
            setSORTING(sortingDefault);
        }, 123);
    };

    const handleCollapse = (section) => {
        const _newCollapse = {
            ...COLLAPSED,
            [section]: !COLLAPSED[section],
        };
        setCOLLAPSED(_newCollapse);
    };

    return (
        <>
            <AppTooltip
                title={type === "options" ? t("Otwórz ustawienia tablicy") : t("Otwórz sortowanie zaawansowane")}
                arrow
                placement={"top-end"}
                type={"dark"}
                classes={{ tooltip: classes.tooltip }}
            >
                <IconButton
                    role={"menuitem"}
                    onClick={handleOpenSettings}
                    color={"primary"}
                    aria-label={
                        type === "options" ? t("Otwórz ustawienia tablicy") : t("Otwórz sortowanie zaawansowane")
                    }
                >
                    {type && type === "sort" ? <Sort /> : <Settings />}
                </IconButton>
            </AppTooltip>

            <Drawer
                anchor={"right"}
                open={SETTINGS_OPEN}
                classes={{ root: classes.DrawerRoot, paper: classes.DrawerPaper }}
                PaperProps={{
                    onKeyDown: (event) => {
                        if (event?.keyCode === 27) {
                            const _tooltips = document?.querySelectorAll('[role="tooltip"]');
                            for (let i = 0; i < _tooltips.length; i++) {
                                _tooltips[i].innerHTML = "";
                            }
                        }
                    },
                }}
            >
                <Box role="presentation" className={classes.layoutBox}>
                    <Card elevation={0} className={classes.Card}>
                        <CardHeader
                            title={
                                type === "options" ? (
                                    <>
                                        <SettingsOutlined sx={{ marginRight: 1, width: 28, height: 28 }} />
                                        <Box
                                            component={"h2"}
                                            sx={{ fontSize: "inherit", fontWeight: "inherit", my: 0 }}
                                        >
                                            {t("Ustawienia")}
                                        </Box>
                                    </>
                                ) : (
                                    <>
                                        <Sort sx={{ marginRight: 1, width: 28, height: 28 }} />
                                        <Box
                                            component={"h2"}
                                            sx={{ fontSize: "inherit", fontWeight: "inherit", my: 0 }}
                                        >
                                            {t("Sortowanie zaawansowane")}
                                        </Box>
                                    </>
                                )
                            }
                            action={
                                <AppTooltip
                                    title={
                                        type === "options"
                                            ? t("Zamknij ustawienia tablicy")
                                            : t("Zamknij sortowanie zaawansowane")
                                    }
                                    arrow
                                    placement={"left"}
                                    type={"dark"}
                                    classes={{ tooltip: classes.tooltip }}
                                    open={!!CLOSE_TOOLTIP}
                                    onOpen={() => {
                                        setCLOSE_TOOLTIP(true);
                                    }}
                                    onClose={(e, reason) => {
                                        setCLOSE_TOOLTIP(false);
                                    }}
                                    onKeyDown={(event) => {
                                        if (event?.keyCode === 27) setCLOSE_TOOLTIP(false);
                                    }}
                                >
                                    <IconButton
                                        onClick={handleOpenSettings}
                                        aria-label={
                                            type === "options"
                                                ? t("Zamknij ustawienia tablicy")
                                                : t("Zamknij sortowanie zaawansowane")
                                        }
                                        sx={{ mt: 0.375 }}
                                    >
                                        <Close color={"primary"} />
                                    </IconButton>
                                </AppTooltip>
                            }
                            classes={{
                                root: classes.CardHeader,
                                title: classes.CardHeaderTitle,
                                action: classes.CardHeaderActions,
                            }}
                        />
                        <CardContent className={classes.CardContent}>
                            {!!RECORDS_COUNT?.hasOwnProperty("_accepted") ? (
                                !!RECORDS_COUNT?._accepted ? null : (
                                    <Grid container spacing={0}>
                                        <Grid item xs={12}>
                                            <Alert
                                                severity="error"
                                                sx={{ alignItems: RECORDS_COUNT?._notes ? "flex-start" : "center" }}
                                            >
                                                <span
                                                    dangerouslySetInnerHTML={{
                                                        __html: RECORDS_COUNT?._errMessage,
                                                    }}
                                                />
                                                {!!RECORDS_COUNT?._notes && (
                                                    <>
                                                        <small
                                                            style={{
                                                                display: "block",
                                                                width: "100%",
                                                                textAlign: "left",
                                                                marginTop: 16,
                                                                borderTop: "1px solid #CCC",
                                                            }}
                                                            dangerouslySetInnerHTML={{
                                                                __html: RECORDS_COUNT?._notes,
                                                            }}
                                                        />
                                                    </>
                                                )}
                                            </Alert>
                                        </Grid>
                                    </Grid>
                                )
                            ) : null}

                            {type === "options" ? (
                                <>
                                    <Grid container spacing={0} className={classes.sectionWrap}>
                                        <Grid
                                            item
                                            xs={12}
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                mb: COLLAPSED.layout ? 0 : 3,
                                            }}
                                        >
                                            <Typography className={classes.sectionTitle} component={"h3"}>
                                                {t("Układ tablicy")}
                                            </Typography>
                                            {downLg ? (
                                                <IconButton onClick={() => handleCollapse("layout")}>
                                                    {COLLAPSED.layout ? <ExpandMore /> : <ExpandLess />}
                                                </IconButton>
                                            ) : null}
                                        </Grid>
                                        {COLLAPSED.layout ? null : (
                                            <Grid item xs={12} sx={{ mb: dbSettingsDnD ? 2 : 5 }}>
                                                {!!LAYOUT && (
                                                    <>
                                                        {dbSettingsDnD ? (
                                                            <TableLayoutSettingDnD
                                                                layout={LAYOUT}
                                                                onChangeLayout={(newLayout) =>
                                                                    handleChangeLayout({ newLayout })
                                                                }
                                                            />
                                                        ) : (
                                                            <TableLayoutSetting
                                                                layout={LAYOUT}
                                                                onChangeLayout={(newLayout) =>
                                                                    handleChangeLayout({ newLayout })
                                                                }
                                                            />
                                                        )}
                                                    </>
                                                )}
                                            </Grid>
                                        )}
                                    </Grid>

                                    <Divider sx={{ mt: 2.5, mb: 2.5, borderColor: "transparent" }} />

                                    <Grid container spacing={0} className={classes.sectionWrap}>
                                        <Grid
                                            item
                                            xs={12}
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                mb: COLLAPSED.options ? 0 : 1,
                                            }}
                                        >
                                            <Typography className={classes.sectionTitle} component={"h3"}>
                                                {t("Opcje tablicy")}
                                            </Typography>
                                            {downLg ? (
                                                <IconButton onClick={() => handleCollapse("options")}>
                                                    {COLLAPSED.options ? <ExpandMore /> : <ExpandLess />}
                                                </IconButton>
                                            ) : null}
                                        </Grid>
                                        {COLLAPSED.options ? null : (
                                            <Grid item xs={12}>
                                                {!!OPTIONS && (
                                                    <TableOptionsSetting
                                                        options={OPTIONS}
                                                        onChangeOptions={(newOptions) =>
                                                            handleChangeOptions({ newOptions })
                                                        }
                                                    />
                                                )}
                                            </Grid>
                                        )}
                                    </Grid>
                                </>
                            ) : (
                                <Grid container spacing={0} sx={{ mt: 0 }} className={classes.sectionSortWrap}>
                                    <Grid
                                        item
                                        xs={12}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            mb: COLLAPSED.sorting ? 0 : 3,
                                            mt: 0.5,
                                        }}
                                    >
                                        <Typography className={classes.sectionTitle} component={"h3"}>
                                            {t("Sortowanie tablicy")}
                                        </Typography>
                                        {downLg ? (
                                            <IconButton onClick={() => handleCollapse("sorting")}>
                                                {COLLAPSED.sorting ? <ExpandMore /> : <ExpandLess />}
                                            </IconButton>
                                        ) : null}
                                    </Grid>
                                    {COLLAPSED.sorting ? null : (
                                        <Grid item xs={12}>
                                            {!!SORTING && !!LAYOUT && !!OPTIONS && (
                                                <>
                                                    {dbSettingsDnD ? (
                                                        <TableSortingSettingDnD
                                                            headData={headData}
                                                            layout={LAYOUT}
                                                            options={OPTIONS}
                                                            onChangeSorting={(newSorting) =>
                                                                handleChangeSorting({ newSorting })
                                                            }
                                                            sorting={SORTING}
                                                        />
                                                    ) : (
                                                        <TableSortingSetting
                                                            headData={headData}
                                                            layout={LAYOUT}
                                                            options={OPTIONS}
                                                            onChangeSorting={(newSorting) =>
                                                                handleChangeSorting({ newSorting })
                                                            }
                                                            sorting={SORTING}
                                                        />
                                                    )}
                                                </>
                                            )}
                                        </Grid>
                                    )}
                                </Grid>
                            )}
                        </CardContent>
                        <CardActions className={classes.CardActions}>
                            <Button
                                color={"primary"}
                                onClick={type === "options" ? handleReset : handleResetSort}
                                size={"small"}
                                sx={{ fontSize: "0.777777778rem", height: 51 }}
                                disabled={IS_BTN_DISABLED.reset}
                            >
                                {t("Zresetuj")}
                            </Button>
                            <Button
                                color={"primary"}
                                variant={"contained"}
                                onClick={type === "options" ? handleApply : handleApplySort}
                                // size={"small"}
                                sx={{ fontSize: "0.777777778rem", height: 51 }}
                                disabled={!prediction?.table?._accept && IS_BTN_DISABLED.accept}
                            >
                                {t("Zatwierdź")}
                            </Button>
                        </CardActions>
                    </Card>
                </Box>
            </Drawer>
        </>
    );
};

export default TableSettings;
